// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module ReactNativeIndex
 * @package @geenius/analytics/react-native
 * @description React Native analytics surface using native host primitives,
 * StyleSheet-compatible styles, accessibility labels, and testID selectors.
 *
 * Importing this barrel pulls `react` and `react-native` at module load
 * (the value imports below). Consumers that need the public types but
 * cannot resolve `react-native` at runtime (SSR, web Storybook setups,
 * Node test harnesses, type-only barrels) must import from the dedicated
 * type-only entry point:
 *
 * ```ts
 * import type { AnalyticsProviderProps } from "@geenius/analytics/react-native/types"
 * ```
 *
 * That subpath resolves to an empty JavaScript module at runtime and
 * loads only the `.d.ts` declarations. See `./types.ts` and
 * audit-finding #214.
 */

import {
  type AnalyticsConfig,
  type AnalyticsDashboardData,
  type AnalyticsEvent,
  analyzeFunnel,
  type ChartDataPoint,
  type ConsentSettings,
  createTracker,
  defaultAnalyticsConfig,
  type FunnelResult,
  type FunnelStep,
  getAnalyticsDeviceLabel,
  type MetricSummary,
  pluralizeAnalytics,
  type RealtimeStats,
  type TimelineEvent,
  type TrackerInstance,
  translateAnalytics,
  type UserTraits,
} from "@geenius/analytics";
import { invariants } from "@geenius/tokens";
import {
  createContext,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  FlatList,
  type ListRenderItem,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  View,
} from "react-native";
import {
  type AnalyticsContextOptions,
  createNativeAnalyticsActions,
  type NativeAnalyticsActions,
  resolveAnalyticsTracker,
} from "./actions";
import {
  type ConsentPreferenceKey,
  type ConsentPreferenceState,
  consentCategories,
  createAcceptAllConsent,
  createConsentSettings,
  createInitialConsentPreferences,
  createRejectOptionalConsent,
  updateConsentPreference,
} from "./consent";

type NativeAnalyticsParams = Record<string, string | number>;

const CONSENT_CATEGORY_LABEL_KEYS: Record<ConsentPreferenceKey, string> = {
  functional: "analytics.consent.functional",
  analytics: "analytics.consent.analyticsScope",
  marketing: "analytics.consent.marketing",
  personalization: "analytics.consent.personalization",
};

function tokenPx(value: string | undefined, fallback: number): number {
  const parsed = Number.parseFloat(value ?? "");
  return Number.isFinite(parsed) ? parsed : fallback;
}

const nativeTokens = {
  radius: {
    md: tokenPx(invariants.radius.md, 10),
  },
  space: {
    2: tokenPx(invariants.spacing[2], 8),
    4: tokenPx(invariants.spacing[4], 16),
  },
};

function createNativeAnalyticsCopy(): {
  t: (key: string, params?: NativeAnalyticsParams) => string;
  plural: (
    key: string,
    count: number,
    params?: NativeAnalyticsParams,
  ) => string;
  deviceLabel: (device: string) => string;
} {
  return {
    t: translateAnalytics,
    plural: pluralizeAnalytics,
    deviceLabel: (device) => getAnalyticsDeviceLabel(device),
  };
}

/** Re-exported React Native `StyleSheet` for consumer convenience. */
export { StyleSheet };

/** Shared StyleSheet entries used by the native analytics components. */
export const nativeStyles = StyleSheet.create({
  stack: { gap: nativeTokens.space[2], padding: nativeTokens.space[4] },
  row: { alignItems: "center", flexDirection: "row", gap: nativeTokens.space[2] },
  title: { fontSize: 18, fontWeight: "700" },
  muted: { opacity: 0.7 },
  card: { borderRadius: nativeTokens.radius.md, padding: nativeTokens.space[4] },
  state: { gap: nativeTokens.space[2] },
  stateAction: { borderRadius: nativeTokens.radius.md, padding: nativeTokens.space[2] },
  consentCategory: {
    alignItems: "center",
    flexDirection: "row",
    gap: nativeTokens.space[2],
  },
  skeletonLine: {
    borderRadius: nativeTokens.radius.md,
    opacity: 0.35,
    padding: nativeTokens.space[2],
  },
  skeletonText: { opacity: 0.5 },
});

interface NativeStateViewProps {
  actionLabel?: string;
  message?: string;
  onAction?: () => void;
  testID: string;
  title: string;
}

function getErrorMessage(error: Error | string): string {
  return typeof error === "string" ? error : error.message;
}

function NativeStateView(props: NativeStateViewProps): ReactElement {
  const copy = createNativeAnalyticsCopy();
  const actionLabel = props.actionLabel ?? copy.t("analytics.actions.retry");

  return (
    <View
      testID={props.testID}
      accessibilityLabel={props.title}
      style={[nativeStyles.card, nativeStyles.state]}
    >
      <Text style={nativeStyles.title}>{props.title}</Text>
      {props.message ? (
        <Text style={nativeStyles.muted}>{props.message}</Text>
      ) : null}
      {props.onAction ? (
        <Pressable
          testID={`${props.testID}-action`}
          accessibilityLabel={actionLabel}
          onPress={props.onAction}
          style={nativeStyles.stateAction}
        >
          <Text>{actionLabel}</Text>
        </Pressable>
      ) : null}
    </View>
  );
}

function NativeSkeleton(props: {
  label: string;
  rows?: number;
  testID: string;
}): ReactElement {
  const copy = createNativeAnalyticsCopy();
  const rows = Array.from({ length: props.rows ?? 3 }, (_, index) => index);
  return (
    <View
      testID={props.testID}
      accessibilityLabel={props.label}
      style={[nativeStyles.card, nativeStyles.state]}
    >
      <Text style={nativeStyles.muted}>{props.label}</Text>
      {rows.map((row) => (
        <View
          key={row}
          testID={`${props.testID}-row-${row}`}
          style={nativeStyles.skeletonLine}
        >
          <Text style={nativeStyles.skeletonText}>
            {copy.t("analytics.topPages.loading")}
          </Text>
        </View>
      ))}
    </View>
  );
}

/** Props accepted by {@link AnalyticsProvider}. */
export interface AnalyticsProviderProps {
  config?: AnalyticsConfig;
  tracker?: TrackerInstance;
  initialConsent?: ConsentSettings | null;
  children?: ReactNode;
}

/** Options accepted by {@link useAnalyticsContext}. */
export interface UseAnalyticsContextOptions extends AnalyticsContextOptions {}

/** Native consent state exposed by {@link AnalyticsProvider}. */
export interface AnalyticsConsentContextValue {
  consentSettings: ConsentSettings | null;
  updateConsent: (settings: ConsentSettings) => void;
}

const AnalyticsContext = createContext<TrackerInstance | null>(null);
const AnalyticsConsentContext =
  createContext<AnalyticsConsentContextValue | null>(null);

/** Provides a shared analytics tracker to descendant React Native components. */
export function AnalyticsProvider(props: AnalyticsProviderProps): ReactElement {
  const tracker = useMemo(
    () =>
      props.tracker ?? createTracker(props.config ?? defaultAnalyticsConfig),
    [props.config, props.tracker],
  );
  const [consentSettings, setConsentSettings] =
    useState<ConsentSettings | null>(props.initialConsent ?? null);
  const updateConsent = useCallback(
    (settings: ConsentSettings) => {
      setConsentSettings(settings);
      void tracker.consent(settings);
    },
    [tracker],
  );
  const consentContext = useMemo<AnalyticsConsentContextValue>(
    () => ({ consentSettings, updateConsent }),
    [consentSettings, updateConsent],
  );

  useEffect(() => {
    if (props.initialConsent) {
      void tracker.consent(props.initialConsent);
    }
  }, [props.initialConsent, tracker]);

  return (
    <AnalyticsContext.Provider value={tracker}>
      <AnalyticsConsentContext.Provider value={consentContext}>
        {props.children}
      </AnalyticsConsentContext.Provider>
    </AnalyticsContext.Provider>
  );
}

/** Resolves the analytics tracker from context, or `null` when optional and unmounted. */
export function useAnalyticsContext(
  options: UseAnalyticsContextOptions = {},
): TrackerInstance | null {
  const tracker = useContext(AnalyticsContext);
  return resolveAnalyticsTracker(tracker, options);
}

/** Reads the native consent state from the nearest analytics provider. */
export function useAnalyticsConsent(): AnalyticsConsentContextValue | null {
  return useContext(AnalyticsConsentContext);
}

/** Native analytics action surface returned by {@link useAnalytics}. */
export interface UseAnalyticsReturn extends NativeAnalyticsActions {}

/** Returns native analytics actions bound to the context tracker (or a fresh one). */
export function useAnalytics(
  config: AnalyticsConfig = defaultAnalyticsConfig,
): UseAnalyticsReturn {
  const contextTracker = useAnalyticsContext({ optional: true });
  const fallbackTracker = useMemo(() => createTracker(config), [config]);
  const tracker = contextTracker ?? fallbackTracker;
  return createNativeAnalyticsActions(tracker);
}

/** Fires a `page` event for `url` whenever it changes. */
export function usePageView(url: string, config?: AnalyticsConfig): void {
  const analytics = useAnalytics(config);
  useEffect(() => {
    void analytics.page(url);
  }, [analytics, url]);
}

/** Return shape for {@link useFunnel}. */
export interface UseFunnelReturn {
  result: FunnelResult | null;
  analyze: (events: AnalyticsEvent[]) => FunnelResult;
}

/** Provides a memoised `analyze` callback for funnel evaluation on demand. */
export function useFunnel(name: string, steps: FunnelStep[]): UseFunnelReturn {
  const [result, setResult] = useState<FunnelResult | null>(null);
  const analyze = useCallback(
    (events: AnalyticsEvent[]) => {
      const nextResult = analyzeFunnel(events, steps, name);
      setResult(nextResult);
      return nextResult;
    },
    [name, steps],
  );
  return { result, analyze };
}

/** Convenience hook exposing `identify` and `reset` from the active tracker. */
export function useIdentify(config?: AnalyticsConfig): {
  identify: (userId: string, traits?: UserTraits) => Promise<void>;
  reset: () => Promise<void>;
} {
  const analytics = useAnalytics(config);
  return { identify: analytics.identify, reset: analytics.reset };
}

/** Loads realtime stats once via `fetcher` and exposes loading state. */
export interface UseRealtimeStatsOptions {
  /** Poll interval in milliseconds. Defaults to 5000. Pass 0 to disable polling. */
  intervalMs?: number;
}

export function useRealtimeStats(
  fetcher?: () => Promise<RealtimeStats>,
  options?: UseRealtimeStatsOptions,
): {
  stats: RealtimeStats | null;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
} {
  const [stats, setStats] = useState<RealtimeStats | null>(null);
  const [isLoading, setLoading] = useState(Boolean(fetcher));
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    if (!fetcher) return;
    try {
      const next = await fetcher();
      setStats(next);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [fetcher]);

  useEffect(() => {
    if (!fetcher) return;
    let active = true;
    void refresh();
    const interval = options?.intervalMs ?? 5000;
    if (interval <= 0) {
      return () => {
        active = false;
      };
    }
    const timer = setInterval(() => {
      if (active) void refresh();
    }, interval);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [fetcher, refresh, options?.intervalMs]);

  return { stats, isLoading, error, refresh };
}

/** Props for {@link StatsCard}. */
export interface StatsCardProps {
  label: string;
  value: string | number;
  change?: string;
  trend?: "up" | "down" | "flat";
}

/** Native card displaying a labelled headline metric and optional trend. */
export function StatsCard(props: StatsCardProps): ReactElement {
  return (
    <View
      testID="analytics-stats-card"
      accessibilityLabel={props.label}
      style={nativeStyles.card}
    >
      <Text style={nativeStyles.muted}>{props.label}</Text>
      <Text style={nativeStyles.title}>{String(props.value)}</Text>
      {props.change ? (
        <Text testID="analytics-stats-change">
          {`${props.change} ${props.trend ?? "flat"}`}
        </Text>
      ) : null}
    </View>
  );
}

/** Native card rendering a single {@link MetricSummary}. */
export function MetricCard(props: { metric: MetricSummary }): ReactElement {
  return (
    <View
      testID="analytics-metric-card"
      accessibilityLabel={props.metric.label}
      style={nativeStyles.card}
    >
      <Text style={nativeStyles.muted}>{props.metric.label}</Text>
      <Text style={nativeStyles.title}>{String(props.metric.value)}</Text>
    </View>
  );
}

/** Native card listing chart data points as labelled rows. */
export function ChartCard(props: {
  title: string;
  data: ChartDataPoint[];
}): ReactElement {
  return (
    <View
      testID="analytics-chart-card"
      accessibilityLabel={props.title}
      style={nativeStyles.card}
    >
      <Text style={nativeStyles.title}>{props.title}</Text>
      {props.data.map((point) => (
        <Text key={point.label} testID={`analytics-chart-${point.label}`}>
          {`${point.label}: ${point.value}`}
        </Text>
      ))}
    </View>
  );
}

/** Props for {@link EventTable}. */
interface EventTableProps {
  emptyMessage?: string;
  error?: Error | string | null;
  events: AnalyticsEvent[];
  maxRows?: number;
  isLoading?: boolean;
  onRetry?: () => void;
}

/** Native list rendering recent analytics events with loading, error, and empty states. */
export function EventTable(props: EventTableProps): ReactElement {
  const copy = createNativeAnalyticsCopy();

  if (props.isLoading) {
    return (
      <NativeSkeleton
        testID="analytics-event-list-loading"
        label={copy.t("analytics.events.loading")}
      />
    );
  }

  if (props.error) {
    return (
      <NativeStateView
        testID="analytics-event-list-error"
        title={copy.t("analytics.events.unavailable")}
        message={getErrorMessage(props.error)}
        onAction={props.onRetry}
        actionLabel={copy.t("analytics.events.retry")}
      />
    );
  }

  const events = props.events.slice(0, props.maxRows ?? props.events.length);
  if (events.length === 0) {
    return (
      <NativeStateView
        testID="analytics-event-list-empty"
        title={copy.t("analytics.events.empty")}
        message={
          props.emptyMessage ?? copy.t("analytics.events.emptyDescription")
        }
      />
    );
  }

  const renderEvent: ListRenderItem<AnalyticsEvent> = ({ item }) => (
    <Text testID={`analytics-event-${item.id}`}>{item.name}</Text>
  );

  return (
    <FlatList
      testID="analytics-event-list"
      accessibilityLabel={copy.t("analytics.dashboard.recentEvents")}
      data={events}
      keyExtractor={(event) => event.id}
      renderItem={renderEvent}
      style={nativeStyles.card}
    />
  );
}

/** Props for {@link TopPagesTable}. */
interface TopPagesTableProps {
  emptyMessage?: string;
  error?: Error | string | null;
  isLoading?: boolean;
  onRetry?: () => void;
  pages: Array<{
    url: string;
    views: number;
    uniqueVisitors?: number;
    averageTimeMs?: number;
    averageTimeSeconds?: number;
    averageTime?: string;
  }>;
}

function formatAverageTime(page: TopPagesTableProps["pages"][number]): string {
  if (page.averageTime) return page.averageTime;
  if (typeof page.averageTimeSeconds === "number") {
    return `${Math.round(page.averageTimeSeconds)}s`;
  }
  if (typeof page.averageTimeMs === "number") {
    return `${Math.round(page.averageTimeMs / 1000)}s`;
  }
  return "—";
}

/** Native list of top-viewed pages with loading, error, and empty states. */
export function TopPagesTable(props: TopPagesTableProps): ReactElement {
  const copy = createNativeAnalyticsCopy();

  if (props.isLoading) {
    return (
      <NativeSkeleton
        testID="analytics-top-pages-loading"
        label={copy.t("analytics.topPages.loadingDetailed")}
      />
    );
  }

  if (props.error) {
    return (
      <NativeStateView
        testID="analytics-top-pages-error"
        title={copy.t("analytics.topPages.unavailable")}
        message={getErrorMessage(props.error)}
        onAction={props.onRetry}
        actionLabel={copy.t("analytics.topPages.retry")}
      />
    );
  }

  if (props.pages.length === 0) {
    return (
      <NativeStateView
        testID="analytics-top-pages-empty"
        title={copy.t("analytics.topPages.empty")}
        message={
          props.emptyMessage ?? copy.t("analytics.topPages.emptyDescription")
        }
      />
    );
  }

  const pageHeader = copy.t("analytics.topPages.columns.page");
  const viewsHeader = copy.t("analytics.topPages.columns.views");
  const visitorsHeader = copy.t("analytics.dashboard.uniqueVisitors");
  const timeHeader = copy.t("analytics.topPages.columns.averageTime");

  return (
    <View
      testID="analytics-top-pages"
      accessibilityLabel={copy.t("analytics.topPages.title")}
      style={nativeStyles.card}
    >
      <View
        testID="analytics-top-pages-header"
        accessibilityLabel={copy.t("analytics.topPages.title")}
        style={nativeStyles.row}
      >
        <Text style={nativeStyles.muted}>{pageHeader}</Text>
        <Text style={nativeStyles.muted}>{viewsHeader}</Text>
        <Text style={nativeStyles.muted}>{visitorsHeader}</Text>
        <Text style={nativeStyles.muted}>{timeHeader}</Text>
      </View>
      {props.pages.map((page) => (
        <View
          key={page.url}
          testID={`analytics-page-${page.url}`}
          style={nativeStyles.row}
        >
          <Text accessibilityLabel={pageHeader}>{page.url}</Text>
          <Text accessibilityLabel={viewsHeader}>{String(page.views)}</Text>
          <Text accessibilityLabel={visitorsHeader}>
            {String(page.uniqueVisitors ?? 0)}
          </Text>
          <Text accessibilityLabel={timeHeader}>{formatAverageTime(page)}</Text>
        </View>
      ))}
    </View>
  );
}

/** Native card rendering a desktop/tablet/mobile breakdown of sessions. */
export function DeviceBreakdown(props: {
  breakdown: AnalyticsDashboardData["deviceBreakdown"];
}): ReactElement {
  const copy = createNativeAnalyticsCopy();

  return (
    <View
      testID="analytics-device-breakdown"
      accessibilityLabel={copy.t("analytics.devices.breakdown")}
      style={nativeStyles.card}
    >
      <Text>{`${copy.deviceLabel("desktop")} ${props.breakdown.desktop}`}</Text>
      <Text>{`${copy.deviceLabel("tablet")} ${props.breakdown.tablet}`}</Text>
      <Text>{`${copy.deviceLabel("mobile")} ${props.breakdown.mobile}`}</Text>
    </View>
  );
}

/** Native badge showing the current active-visitor count with a loading fallback. */
export function RealtimeBadge(props: {
  stats: RealtimeStats | null;
  isLoading?: boolean;
}): ReactElement {
  const copy = createNativeAnalyticsCopy();
  const label = props.isLoading
    ? copy.t("analytics.realtime.loading")
    : `${props.stats?.activeVisitors ?? 0} ${copy.t("analytics.realtime.activeNow")}`;
  return (
    <Text testID="analytics-realtime-badge" accessibilityLabel={label}>
      {label}
    </Text>
  );
}

/** Scrollable native timeline of recent {@link TimelineEvent} entries. */
export function Timeline(props: {
  events: TimelineEvent[];
  maxVisible?: number;
}): ReactElement {
  const copy = createNativeAnalyticsCopy();

  return (
    <ScrollView
      testID="analytics-timeline"
      accessibilityLabel={copy.t("analytics.events.timeline")}
    >
      {props.events
        .slice(0, props.maxVisible ?? props.events.length)
        .map((event) => (
          <Text key={event.id} testID={`analytics-timeline-${event.id}`}>
            {event.event}
          </Text>
        ))}
    </ScrollView>
  );
}

function formatPercentage(value: number): number {
  // Funnel conversion/dropoff rates may be authored as 0-1 fractions or 0-100
  // percentages depending on the upstream provider. Normalize for display.
  if (!Number.isFinite(value) || value <= 0) return 0;
  return value <= 1 ? Math.round(value * 100) : Math.round(value);
}

/** Native card rendering each step of a {@link FunnelResult} with conversion/drop-off detail. */
export function FunnelChart(props: { result: FunnelResult }): ReactElement {
  const copy = createNativeAnalyticsCopy();
  const overallPercent = formatPercentage(props.result.overallConversionRate);

  return (
    <View
      testID="analytics-funnel-chart"
      accessibilityLabel={props.result.name}
      style={nativeStyles.card}
    >
      <Text style={nativeStyles.title}>{props.result.name}</Text>
      <Text
        testID="analytics-funnel-overall"
        accessibilityLabel={copy.t("analytics.funnel.overall", {
          percentage: overallPercent,
        })}
      >
        {copy.t("analytics.funnel.overall", { percentage: overallPercent })}
      </Text>
      {props.result.steps.map((step) => {
        const conversionPercent = formatPercentage(step.conversionRate);
        const dropoffPercent = formatPercentage(step.dropoffRate);
        return (
          <View
            key={step.name}
            testID={`analytics-funnel-step-${step.name}`}
            style={nativeStyles.stack}
          >
            <Text>{`${step.name}: ${step.count}`}</Text>
            <Text
              testID={`analytics-funnel-step-${step.name}-conversion`}
              accessibilityLabel={copy.t("analytics.funnel.overall", {
                percentage: conversionPercent,
              })}
            >
              {copy.t("analytics.funnel.overall", {
                percentage: conversionPercent,
              })}
            </Text>
            <Text
              testID={`analytics-funnel-step-${step.name}-dropoff`}
              accessibilityLabel={copy.t("analytics.funnel.dropoff", {
                percentage: dropoffPercent,
              })}
            >
              {copy.t("analytics.funnel.dropoff", {
                percentage: dropoffPercent,
              })}
            </Text>
          </View>
        );
      })}
    </View>
  );
}

/** Props for {@link ConsentBanner}. */
interface ConsentBannerProps {
  initialSettings?: Partial<ConsentSettings>;
  onAccept?: (settings: ConsentSettings) => void;
  onReject?: (settings: ConsentSettings) => void;
  onSavePreferences?: (settings: ConsentSettings) => void;
  show?: boolean;
}

/** Native consent banner with per-category switches, accept-all, save, and reject actions. */
export function ConsentBanner(props: ConsentBannerProps): ReactElement | null {
  const [visible, setVisible] = useState(props.show ?? true);
  const [preferences, setPreferences] = useState<ConsentPreferenceState>(() =>
    createInitialConsentPreferences(props.initialSettings),
  );
  const consent = useAnalyticsConsent();
  const copy = createNativeAnalyticsCopy();

  if (!visible) return null;

  const updatePreference = (key: ConsentPreferenceKey, value: boolean) => {
    setPreferences((current) => updateConsentPreference(current, key, value));
  };

  const acceptAll = () => {
    const settings = createAcceptAllConsent();
    consent?.updateConsent(settings);
    props.onAccept?.(settings);
    setVisible(false);
  };

  const savePreferences = () => {
    const settings = createConsentSettings(preferences);
    consent?.updateConsent(settings);
    props.onSavePreferences?.(settings);
    props.onAccept?.(settings);
    setVisible(false);
  };

  const rejectOptional = () => {
    const settings = createRejectOptionalConsent();
    consent?.updateConsent(settings);
    props.onReject?.(settings);
    setVisible(false);
  };

  return (
    <View
      testID="analytics-consent-banner"
      accessibilityLabel={copy.t("analytics.consent.regionLabel")}
      style={nativeStyles.card}
    >
      <Text>{copy.t("analytics.consent.regionLabel")}</Text>
      {consentCategories.map((category) => {
        const label = copy.t(CONSENT_CATEGORY_LABEL_KEYS[category.key]);

        return (
          <View
            key={category.key}
            testID={`analytics-consent-category-${category.testID}`}
            accessibilityLabel={copy.t("analytics.consent.categoryLabel", {
              label,
            })}
            style={nativeStyles.consentCategory}
          >
            <Text>{label}</Text>
            <Switch
              testID={`analytics-consent-toggle-${category.testID}`}
              accessibilityLabel={copy.t("analytics.consent.toggleLabel", {
                label,
              })}
              value={preferences[category.key]}
              onValueChange={(value) => updatePreference(category.key, value)}
            />
          </View>
        );
      })}
      <Pressable
        testID="analytics-consent-accept"
        accessibilityLabel={copy.t("analytics.consent.acceptAll")}
        onPress={acceptAll}
      >
        <Text>{copy.t("analytics.consent.acceptAll")}</Text>
      </Pressable>
      <Pressable
        testID="analytics-consent-save-preferences"
        accessibilityLabel={copy.t("analytics.consent.savePreferences")}
        onPress={savePreferences}
      >
        <Text>{copy.t("analytics.consent.savePreferences")}</Text>
      </Pressable>
      <Pressable
        testID="analytics-consent-reject"
        accessibilityLabel={copy.t("analytics.consent.reject")}
        onPress={rejectOptional}
      >
        <Text>{copy.t("analytics.consent.reject")}</Text>
      </Pressable>
    </View>
  );
}

/** Widget descriptor consumed by {@link DashboardBuilder}. */
export interface DashboardBuilderWidget {
  id: string;
  title: string;
  /** Optional pre-rendered ReactElement; if omitted, only the legend is shown. */
  render?: () => ReactElement;
  /** Optional description rendered under the widget title. */
  description?: string;
}

/** Layout descriptor consumed by {@link DashboardBuilder}. */
export interface DashboardBuilderLayout {
  title: string;
  widgets?: DashboardBuilderWidget[];
}

/** Native dashboard composition shell — renders title + widget sections with legends. */
export function DashboardBuilder(props: {
  layout: DashboardBuilderLayout;
}): ReactElement {
  const copy = createNativeAnalyticsCopy();
  const widgets = props.layout.widgets ?? [];

  return (
    <View
      testID="analytics-dashboard-builder"
      accessibilityLabel={props.layout.title}
      style={nativeStyles.stack}
    >
      <Text style={nativeStyles.title}>{props.layout.title}</Text>
      {widgets.map((widget) => {
        const legend = copy.t("analytics.dashboardBuilder.widgetLegend", {
          title: widget.title,
        });
        return (
          <View
            key={widget.id}
            testID={`analytics-dashboard-widget-${widget.id}`}
            accessibilityLabel={legend}
            style={nativeStyles.card}
          >
            <Text style={nativeStyles.title}>{widget.title}</Text>
            {widget.description ? (
              <Text style={nativeStyles.muted}>{widget.description}</Text>
            ) : null}
            {widget.render ? widget.render() : null}
          </View>
        );
      })}
    </View>
  );
}

/** Props for {@link AnalyticsDashboardPage}. */
interface AnalyticsDashboardPageProps {
  data?: AnalyticsDashboardData;
  emptyMessage?: string;
  error?: Error | string | null;
  isLoading?: boolean;
  onRetry?: () => void;
  recentEvents?: AnalyticsEvent[];
  /** Optional chart datapoints rendered above the funnel section. */
  chart?: { title: string; data: ChartDataPoint[] };
  /** Optional precomputed funnel result rendered in the conversion section. */
  funnel?: FunnelResult;
  /** Optional timeline events rendered in the activity section. */
  timeline?: TimelineEvent[];
  /** Optional realtime snapshot rendered in the realtime badge. */
  realtimeStats?: RealtimeStats | null;
  isRealtimeLoading?: boolean;
}

/** Native dashboard page composing the analytics widgets with loading/error/empty states. */
export function AnalyticsDashboardPage(
  props: AnalyticsDashboardPageProps,
): ReactElement {
  const copy = createNativeAnalyticsCopy();

  if (props.isLoading) {
    return (
      <NativeSkeleton
        testID="analytics-dashboard-page-loading"
        label={copy.t("analytics.dashboard.loading")}
        rows={4}
      />
    );
  }

  if (props.error) {
    return (
      <NativeStateView
        testID="analytics-dashboard-page-error"
        title={copy.t("analytics.dashboard.unavailable")}
        message={getErrorMessage(props.error)}
        onAction={props.onRetry}
        actionLabel={copy.t("analytics.dashboard.retry")}
      />
    );
  }

  if (!props.data) {
    return (
      <NativeStateView
        testID="analytics-dashboard-page-empty"
        title={copy.t("analytics.dashboard.emptyTitle")}
        message={
          props.emptyMessage ?? copy.t("analytics.dashboard.emptyDescription")
        }
      />
    );
  }

  return (
    <ScrollView
      testID="analytics-dashboard-page"
      accessibilityLabel={copy.t("analytics.dashboard.title")}
      style={nativeStyles.stack}
    >
      <Text style={nativeStyles.title}>
        {copy.t("analytics.dashboard.title")}
      </Text>
      <Text style={nativeStyles.muted}>
        {copy.t("analytics.dashboard.subtitle")}
      </Text>

      <View testID="analytics-dashboard-stats" style={nativeStyles.row}>
        <StatsCard
          label={copy.t("analytics.dashboard.pageViews")}
          value={props.data.totalPageViews}
        />
        <StatsCard
          label={copy.t("analytics.dashboard.uniqueVisitors")}
          value={props.data.uniqueVisitors}
        />
      </View>

      <RealtimeBadge
        stats={props.realtimeStats ?? null}
        isLoading={props.isRealtimeLoading}
      />

      {props.chart ? (
        <ChartCard title={props.chart.title} data={props.chart.data} />
      ) : null}

      <TopPagesTable pages={props.data.topPages} />

      <DeviceBreakdown breakdown={props.data.deviceBreakdown} />

      {props.funnel ? <FunnelChart result={props.funnel} /> : null}

      {props.timeline && props.timeline.length > 0 ? (
        <Timeline events={props.timeline} />
      ) : null}

      <EventTable events={props.recentEvents ?? []} />
    </ScrollView>
  );
}
